import {MouseEvent, useRef, useState, useEffect, SyntheticEvent} from 'react'
import { Cover, MediaFile } from '../../../../entity'
import { RectangleDrawingView } from './covers/rectangle-drawing-view'
import { EllipseDrawingView } from './covers/ellipse-drawing-view'
import { FreehandDrawingView } from './covers/freehand-drawing-view'
import { DrawFreehand } from './covers/draw-freehand'
import { useDispatch, useSelector } from 'react-redux'
import { PHOTO_EDITOR_ACTIONS } from '../../../../action/photos/photos.constants'
import { State } from '../../../../state'
import { EditableRectangle } from './covers/editable-rectangle'
import { EditableEllipse } from './covers/editable-ellipse'
import './video-editor.scss'

interface VideoEditorProps {
    mediaFile: MediaFile
    editable: boolean
    onDrawEnd?: CallableFunction
}

export const VideoEditor = (props: VideoEditorProps) => {
    const dispatch = useDispatch()

    const videoRef = useRef<HTMLDivElement>(null)
    const playerRef = useRef<HTMLVideoElement>(null)

    const [videoChanged, setVideoChanged] = useState(0)
    const [width, setWidth] = useState(0)
    const [height, setHeight] = useState(0)
    const [awidth, setAWidth] = useState(0)
    const [aheight, setAHeight] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [playbackTime, setPlaybackTime] = useState(0)
    const [selectedCover, setSelectedCover] = useState(null as Cover | null)

    const { covers, photo, tool } = useSelector((state: State) => state.editor)

    useEffect(() => {
        const observer = new ResizeObserver(entries => {
            document.querySelector(`.layers-scroll`)!.scrollTo({top: 1000000000});
        });
        observer.observe(document.querySelector(`.layers`)!);
        return () => {
            observer.disconnect();
        }
    }, []);

    useEffect(() => {
        window.addEventListener('resize', onVideoResize);

        if (props.mediaFile) {
            dispatch({ type: PHOTO_EDITOR_ACTIONS.CLEAR, photo: props.mediaFile })
            dispatch({ type: PHOTO_EDITOR_ACTIONS.ACCEPT_COVER, acceptCover: props.mediaFile.metadata?.covers != null && props.mediaFile.metadata?.covers.length > 0 ? false : null })
            dispatch({ type: PHOTO_EDITOR_ACTIONS.INITIALIZE, photo: props.mediaFile })
        }
    }, [])

    useEffect(() => {
        if (selectedCover === null) {
            return
        }
        window.location.href = `#layer--${ selectedCover.id }`
    }, [selectedCover]);

    useEffect(() => {
        if (videoRef.current) {
            setWidth(videoRef.current!!.clientWidth)
            setHeight(videoRef.current!!.clientHeight)
        }
    }, [videoRef, videoRef.current, awidth, aheight, videoChanged]);

    useEffect(() => {
        if (playerRef.current && props.editable) {
            playerRef.current!.playbackRate = 0
            playerRef.current!.play()
        }
    }, [playerRef]);

    useEffect(() => {
        const callback = () => {
            if (props.editable && playerRef.current && playerRef.current!.currentTime) {
                setPlaybackTime(playerRef.current!.currentTime)
                if (playerRef.current!.currentTime >= playerRef.current!.duration - 0.001) {
                    playerRef.current!.playbackRate = 0
                    setIsPlaying(false)
                }
            }
            requestAnimationFrame(callback)
        };
        requestAnimationFrame(callback)
    }, []);

    useEffect(() => {
        const callback = () => {
            if (!props.editable && playerRef.current && playerRef.current!.currentTime) {
                setPlaybackTime(playerRef.current!.currentTime)
            }
            requestAnimationFrame(callback)
        };
        requestAnimationFrame(callback)
    }, []);

    const onVideoLoad = (e: SyntheticEvent<HTMLVideoElement>) => {
        setAWidth(e.currentTarget.videoWidth);
        setAHeight(e.currentTarget.videoHeight);
    }

    const onFreehandButtonClick = async (event: MouseEvent) => {
        event.preventDefault()

        dispatch({ type: PHOTO_EDITOR_ACTIONS.SELECTED_TOOL, tool: tool === 1 ? null : 1 })
        dispatch({ type: PHOTO_EDITOR_ACTIONS.IS_TOOLS_ENABLED, enabled: tool != null ? false : true })
    }

    const onRectangleButtonClick = async (event: MouseEvent) => {
        event.preventDefault()
        dispatch({ type: PHOTO_EDITOR_ACTIONS.SELECTED_TOOL, tool: tool === 2 ? null : 2 })
        dispatch({ type: PHOTO_EDITOR_ACTIONS.IS_TOOLS_ENABLED, enabled: tool != null ? false : true })
    }

    const onEllipseButtonClick = async (event: MouseEvent) => {
        event.preventDefault()
        dispatch({ type: PHOTO_EDITOR_ACTIONS.SELECTED_TOOL, tool: tool === 3 ? null : 3 })
        dispatch({ type: PHOTO_EDITOR_ACTIONS.IS_TOOLS_ENABLED, enabled: tool != null ? false : true })
    }

    const togglePlay = () => {
        if (!props.editable) {
            return
        }
        if (isPlaying) {
            setIsPlaying(false)
            playerRef.current!.playbackRate = 0
        } else {
            setIsPlaying(true)
            playerRef.current!.playbackRate = 1
            playerRef.current!.play()
        }
    }

    const [ dragCover, setDragCover ] = useState(null as Cover | null)
    const [ dragOriginX, setDragOriginX ] = useState(null as number | null)
    const [ dragOriginY, setDragOriginY ] = useState(null as number | null)
    const [ dragOriginLeft, setDragOriginLeft ] = useState(null as number | null)
    const [ dragOriginTop, setDragOriginTop ] = useState(null as number | null)

    const [ action, setAction ] = useState(null as null | string)
    const [ timeline, setTimeline ] = useState(null as null | Cover)
    const [ timelineOriginX, setTimelineOriginX ] = useState(null as null | number)
    const [ timelineOriginLength, setTimelineOriginLength ] = useState(null as null | number)
    const [ timelineOriginFrom, setTimelineOriginFrom ] = useState(null as null | number)

    const coverDragStart = (cover: Cover) => {
        return (event: MouseEvent) => {
            if (!props.editable) {
                return
            }

            setAction(`drag`)
            setDragOriginX(event.pageX)
            setDragOriginY(event.pageY)
            setDragOriginLeft(cover.position!.x)
            setDragOriginTop(cover.position!.y)
            setDragCover(cover)
        }
    }

    const playIndicatorDrag = (event: MouseEvent) => {
        event.preventDefault()
        setAction(`playIndicator`)
        setTimelineOriginX(event.clientX)
        playerRef.current!.playbackRate = 0
        setIsPlaying(false)
        setTimelineOriginFrom(playbackTime)
    }

    const timelineDragStart = (cover: Cover) => {
        return (event: MouseEvent) => {
            event.preventDefault()
            const target = event.currentTarget as HTMLDivElement
            const rect = target.getBoundingClientRect()

            if (rect.x > (event.pageX - 10) && rect.x < (event.pageX + 10)) {
                setAction(`start`)
            } else if ((rect.x + rect.width) > (event.pageX - 10) && (rect.x + rect.width) < (event.pageX + 10)) {
                setAction(`end`)
            } else {
                setAction(`move`)
            }
            setTimeline(cover)
            setTimelineOriginLength(cover.endSecond - cover.startSecond)
            setTimelineOriginX(event.pageX)
            setTimelineOriginFrom(cover.startSecond)
        }
    }

    const onMouseMove = (event: MouseEvent) => {
        event.preventDefault()
        if (action === `move` && timeline !== null && timelineOriginX !== null && timelineOriginFrom !== null && timelineOriginLength !== null) {
            const timelineWidth = document.querySelector(`div.timeline`)!.getBoundingClientRect().width
            let from = timelineOriginFrom + ((event.pageX - timelineOriginX) / timelineWidth * playerRef.current!.duration);
            if (from < 0) {
                from = 0
            }

            if ((from + timelineOriginLength) > playerRef.current!.duration) {
                from = playerRef.current!.duration - timelineOriginLength
            }
            dispatch({ type: PHOTO_EDITOR_ACTIONS.UPDATE_COVER, id: timeline.id, from: from, length: timelineOriginLength })
        }

        if (action === `start` && timeline !== null && timelineOriginX !== null && timelineOriginFrom !== null && timelineOriginLength !== null) {
            const timelineWidth = document.querySelector(`div.timeline`)!.getBoundingClientRect().width
            const diff = ((event.pageX - timelineOriginX) / timelineWidth * playerRef.current!.duration);
            let from = timelineOriginFrom + diff
            if (from < 0) {
                from = 0
            }
            dispatch({ type: PHOTO_EDITOR_ACTIONS.UPDATE_COVER, id: timeline.id, from: from, length: timelineOriginLength - diff })
        }

        if (action === `end` && timeline !== null && timelineOriginX !== null && timelineOriginFrom !== null && timelineOriginLength !== null) {
            const timelineWidth = document.querySelector(`div.timeline`)!.getBoundingClientRect().width
            let diff = ((event.pageX - timelineOriginX) / timelineWidth * playerRef.current!.duration);
            const from = timelineOriginFrom;
            if ((from + timelineOriginLength + diff) > playerRef.current!.duration) {
                diff = playerRef.current!.duration - timelineOriginLength - from
            }
            dispatch({ type: PHOTO_EDITOR_ACTIONS.UPDATE_COVER, id: timeline.id, from: from, length: timelineOriginLength + diff })
        }

        if (action === `playIndicator` && timelineOriginX !== null && timelineOriginFrom !== null) {
            const timelineWidth = document.querySelector(`div.scale`)!.getBoundingClientRect().width
            const diff = ((event.pageX - timelineOriginX) / timelineWidth * playerRef.current!.duration);
            playerRef.current!.currentTime = (timelineOriginFrom + diff);
        }

        if (action === `drag` && dragOriginX !== null && dragOriginY !== null && dragCover !== null && dragOriginLeft !== null && dragOriginTop !== null) {
            const diffX = ((event.pageX - dragOriginX) * props.mediaFile.metadata!.width / playerRef.current!.clientWidth);
            const diffY = ((event.pageY - dragOriginY) * props.mediaFile.metadata!.height / playerRef.current!.clientHeight)
            dispatch({ type: PHOTO_EDITOR_ACTIONS.UPDATE_COVER, id: dragCover.id, x: dragOriginLeft + diffX, y: dragOriginTop + diffY })
        }
    }

    const onVideoResize = () => {
        setVideoChanged(new Date().getTime())
    }

    const mouseLeave = (event: MouseEvent) => {
        if (!props.editable) {
            return
        }
        event.preventDefault()
        setTimeline(null)
        setTimelineOriginX(null)
        setTimelineOriginLength(null)
        setTimelineOriginFrom(null)
        setAction(null)
    }

    const onTimeLineChange = (event: MouseEvent) => {
        if (action === null) {
            const rect = document.querySelector(`.scale`)?.getBoundingClientRect();
            let pos = event.clientX - (rect?.x || 0);
            if (pos < 0) {
                pos = 0;
            }
            const percent = rect === undefined ? 0 : pos / (rect.width + 6);
            playerRef.current!.currentTime = playerRef.current!.duration * percent;
        }
    }

    const onMouseUp = (event: MouseEvent) => {
        if (!props.editable) {
            return
        }
        event.preventDefault()
        setTimeline(null)
        setTimelineOriginX(null)
        setTimelineOriginLength(null)
        setTimelineOriginFrom(null)
        setAction(null)
        setDragOriginLeft(null)
        setDragOriginTop(null)
        setDragCover(null)
    }

    return (
        <div className="video-editor" onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
            {props.editable ? (
                <div className="video-editor-tools">
                    <button className={'button-icon ' + `${tool === 1 || tool == null ? '' : 'hidden'}`} onClick={onFreehandButtonClick}>
                        <img alt="Freehand icon" src="/image/ic_freehand.svg" />
                    </button>

                    <button className={'button-icon ' + `${tool === 2 || tool == null ? '' : 'hidden'}`} onClick={onRectangleButtonClick}>
                        <img alt="Rectangle icon" src="/image/ic_rectangle.svg" />
                    </button>

                    <button className={'button-icon ' + `${tool === 3 || tool == null ? '' : 'hidden'}`} onClick={onEllipseButtonClick}>
                        <img alt="Ellipse icon" src="/image/ic_ellipse.svg" />
                    </button>
                </div>
            ) : null}

            <div className="video-editor-editor" ref={videoRef} style={{ aspectRatio: (awidth || 1) / (aheight || 1) }}>

                <video onLoadedMetadata={onVideoLoad} className="video" preload="metadata" ref={playerRef} style={{height: `100%`, width: `100%`}} controls={!props.editable} playsInline muted>
                    <source src={props.mediaFile.url + `#t=0.001`} />
                </video>

                {props.editable ? (
                    <div className="editor">
                        {(() => {
                            let tools = []

                            if (tool != null) {
                                if (tool === 1) {
                                    tools.push(
                                        <DrawFreehand
                                            key={'draw_freehand'}
                                            width={width}
                                            height={height}
                                            widthRaito={width / photo!!.metadata!!.width}
                                            onDrawEnd={(cover: Cover) => {
                                                // if(props.onDrawEnd){
                                                //     props.onDrawEnd(cover)
                                                // }
                                                dispatch({ type: PHOTO_EDITOR_ACTIONS.SELECTED_TOOL, tool: null })
                                                dispatch({ type: PHOTO_EDITOR_ACTIONS.IS_TOOLS_ENABLED, enabled: false })
                                                dispatch({ type: PHOTO_EDITOR_ACTIONS.ADD_COVER, cover: cover, from: 0 })
                                            }}
                                        />
                                    )
                                }

                                if (tool === 2) {
                                    tools.push(
                                        <EditableRectangle
                                            key={'editable_rectangle'}
                                            width={width}
                                            height={height}
                                            widthRaito={width / photo!!.metadata!!.width}
                                            onDrawEnd={(cover: Cover) => {
                                                if (props.onDrawEnd) {
                                                    props.onDrawEnd(cover)
                                                }
                                                //dispatch({ type: PHOTO_EDITOR_ACTIONS.ADD_COVER, cover: cover, from: playbackTime })
                                            }}
                                        />
                                    )
                                }

                                if (tool === 3) {
                                    tools.push(
                                        <EditableEllipse
                                            key={'editable_ellipse'}
                                            width={width}
                                            height={height}
                                            widthRaito={width / photo!!.metadata!!.width}
                                            onDrawEnd={(cover: Cover) => {
                                                if (props.onDrawEnd) {
                                                    props.onDrawEnd(cover)
                                                }
                                                //dispatch({ type: PHOTO_EDITOR_ACTIONS.ADD_COVER, cover: cover, from: playbackTime })
                                            }}
                                        />
                                    )
                                }
                            }

                            return tools
                        })()}
                    </div>
                ) : null}

                <div className="covers" style={{ height: height }}>
                    {(() => {
                        let objects = []

                        if (photo != null && photo.metadata != null && covers != null) {
                            const ratioWidth = width / photo.metadata.width
                            const ratioHeight = height / photo.metadata.height

                            for (let i = 0; i < covers.length; i++) {
                                const cover = covers[i]

                                if (cover.type === 1) {
                                    objects.push(<RectangleDrawingView visible={cover.startSecond <= playbackTime && cover.endSecond >= playbackTime} selected={selectedCover !== null && selectedCover.id === cover.id} onMouseDown={coverDragStart(cover)} onClick={() => { if (!props.editable) {
                                        return
                                    } setSelectedCover(cover) }} key={'rd' + cover.id} width={(cover.width ?? 0) * ratioWidth} height={(cover.height ?? 0) * ratioWidth} left={(cover.position?.x ?? 0) * ratioWidth} top={(cover.position?.y ?? 0) * ratioWidth} />)
                                }

                                if (cover.type === 2) {
                                    objects.push(<EllipseDrawingView visible={cover.startSecond <= playbackTime && cover.endSecond >= playbackTime} selected={selectedCover !== null && selectedCover.id === cover.id} onMouseDown={coverDragStart(cover)} onClick={() => { if (!props.editable) {
                                        return
                                    } setSelectedCover(cover) }} key={'ed' + cover.id} width={(cover.width ?? 0) * ratioWidth} height={(cover.height ?? 0) * ratioWidth} left={(cover.position?.x ?? 0) * ratioWidth} top={(cover.position?.y ?? 0) * ratioWidth} />)
                                }

                                if (cover.type === 3) {
                                    objects.push(<FreehandDrawingView visible={cover.startSecond <= playbackTime && cover.endSecond >= playbackTime} selected={selectedCover !== null && selectedCover.id === cover.id} onMouseDown={coverDragStart(cover)} onClick={() => { if (!props.editable) {
                                        return
                                    } setSelectedCover(cover) }} key={'fh' + cover.id} ratioWidth={ratioWidth} ratioHeight={ratioHeight} width={width} height={height} points={cover.points ?? []} left={(cover.position?.x ?? 0) * ratioWidth} top={(cover.position?.y ?? 0) * ratioHeight} />)
                                }
                            }
                        }

                        return objects
                    })()}
                </div>
            </div>

            { props.editable && (
                <>
                    <div className="controls">
                        <img alt="" height={20} src="/start.svg" width={20} onClick={ () => playerRef.current!.currentTime = 0.001 } />
                        <div onClick={togglePlay}>{ isPlaying ? <img alt="" height={40} src="/pause.svg" width={40} /> : <img alt="" height={40} src="/play.svg" width={40} />  }</div>
                        <img alt="" height={20} src="/end.svg" width={20} onClick={ () => playerRef.current!.currentTime = playerRef.current!.duration - 0.001 } />
                    </div>
                    <div className="layers-container" onMouseLeave={mouseLeave}>
                        <div className="play-indicator" style={{ left: `calc(30px + (100% - 38px) * ${ playbackTime / (playerRef.current?.duration || 10) })`}}>
                            <div className="top" onMouseDown={playIndicatorDrag}></div>
                            <div className="line"></div>
                        </div>
                        <div className="scale">
                            { [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(s => (
                                <div style={{ left: `${ s / (playerRef.current?.duration || 10) * 100 }%`, marginLeft: s === 0 ? `4px` : `` }}>{ s }s</div>
                            )) }
                        </div>
                        <div className="layers-scroll" onClick={onTimeLineChange}>
                            <div className="layers" >
                            { covers.map(cover => (
                                <div id={`layer--${ cover.id }`} className={`layer ${selectedCover && selectedCover.id === cover.id ? `layer--selected` : ``}`}>
                                    <div className="delete-layer" onClick={() => dispatch({ type: PHOTO_EDITOR_ACTIONS.DELETE_COVER, id: cover.id })}>
                                        <img alt="" height={24} src="/trash.svg" />
                                    </div>
                                    <div className="timeline" style={{ position: `relative`}}>
                                        <div style={{ left: `${ cover.startSecond / (playerRef.current?.duration || 10) * 100 }%`, width: `${ (cover.endSecond - cover.startSecond) / (playerRef.current?.duration || 10) * 100 }%` }} onClick={() => setSelectedCover(cover)} onMouseDown={timelineDragStart(cover)}>
                                            <div />
                                            <div />
                                        </div>
                                    </div>
                                </div>
                            )) }
                        </div>
                        </div>
                    </div>
                </>
            ) }
        </div>
    )
}
